import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Injector,
  Input,
  OnInit,
  Output
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DevicesClass, DeviceTypes } from '@app/core/classes';
import { BookingService } from '@app/modules/booking-modules/booking.service';
import { InnInfoResult } from '@app/modules/booking-modules/booking.model';
import { GenresModel } from '@app/modules/fundamentals-modules/fundamentals.models';
import { PERFORMER_TYPES } from '@app/modules/performers-modules/client/performer-form.constants';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { finalize } from 'rxjs/operators';
import {
  extractBookingInnError,
  VenueCustomerProfileFieldsLayout,
  VenueInnValidationState
} from './venue-customer-form.constants';

export type { VenueInnValidationState };

@UntilDestroy()
@Component({
  selector: 'app-venue-customer-profile-fields',
  templateUrl: './venue-customer-profile-fields.component.html',
  styleUrls: ['./venue-customer-profile-fields.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VenueCustomerProfileFieldsComponent extends DevicesClass implements OnInit {
  @Input() form!: FormGroup;
  @Input() genres: GenresModel = [];
  @Input() layout: VenueCustomerProfileFieldsLayout = 'dialog';
  @Output() readonly innValidationStateChange = new EventEmitter<VenueInnValidationState>();

  innValidationError = '';
  private innValidationPending = false;
  private lastValidatedInn = '';

  readonly DTypes = DeviceTypes;
  readonly PerformerTypes = PERFORMER_TYPES;
  readonly descriptionMaxLength = 1000;

  constructor(
    injector: Injector,
    public cdr: ChangeDetectorRef,
    private bookingService: BookingService
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.form
      ?.get('inn')
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe(() => this.clearInnValidation());
  }

  descriptionCharsRemaining(): number {
    const len = String(this.form.get('description')?.value ?? '').length;
    return Math.max(0, this.descriptionMaxLength - len);
  }

  onInnBlur(): void {
    const innDigits = String(this.form?.get('inn')?.value ?? '').replace(/\D/g, '');
    if (!/^\d{10}$|^\d{12}$/.test(innDigits)) {
      this.clearInnValidation();
      return;
    }
    if (innDigits === this.lastValidatedInn && !this.innValidationPending && !this.innValidationError) {
      return;
    }
    if (this.innValidationPending) {
      return;
    }

    if (innDigits !== this.lastValidatedInn) {
      this.lastValidatedInn = '';
    }

    this.setInnValidationState('', true);

    this.bookingService
      .validateInn(innDigits)
      .pipe(
        untilDestroyed(this),
        finalize(() => {
          if (this.innValidationPending) {
            this.setInnValidationState(this.innValidationError, false);
            this.cdr.markForCheck();
          }
        })
      )
      .subscribe({
        next: result => this.handleInnValidationSuccess(innDigits, result),
        error: err => this.handleInnValidationError(err)
      });
  }

  private handleInnValidationSuccess(innDigits: string, result: InnInfoResult): void {
    this.lastValidatedInn = innDigits;
    this.setInnValidationState('', false);
    const organizationName = (result.name ?? '').trim();
    if (organizationName) {
      const current = String(this.form.get('venueName')?.value ?? '').trim();
      if (!current) {
        this.form.get('venueName')?.setValue(organizationName);
      }
    }
    this.cdr.markForCheck();
  }

  private handleInnValidationError(err: unknown): void {
    this.lastValidatedInn = '';
    this.setInnValidationState(this.extractInnError(err), false);
    this.cdr.markForCheck();
  }

  private clearInnValidation(): void {
    this.lastValidatedInn = '';
    this.setInnValidationState('', false);
    this.cdr.markForCheck();
  }

  private setInnValidationState(error: string, pending: boolean): void {
    this.innValidationPending = pending;
    this.innValidationError = error;
    const validatedInn = !error ? this.lastValidatedInn : '';
    this.innValidationStateChange.emit({ error, pending, validatedInn });
  }

  private extractInnError(err: unknown): string {
    return extractBookingInnError(err);
  }
}
